import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";

export default function FeedbackDialog({ open, onOpenChange, onDone, closing = false }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [experience, setExperience] = useState("");
  const [feedback, setFeedback] = useState("");
  const [features, setFeatures] = useState("");
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setRating(0); setExperience(""); setFeedback(""); setFeatures(""); setReview(""); };

  const submit = async () => {
    setBusy(true);
    try {
      await api.submitFeedback({ rating, experience, feedback, features, review });
      toast.success("Thank you — your feedback shapes INDIEWOOD!");
      reset();
      onOpenChange(false);
      if (onDone) onDone();
    } catch { toast.error("Could not submit feedback"); }
    finally { setBusy(false); }
  };

  const skip = () => { onOpenChange(false); if (onDone) onDone(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-sans text-2xl font-extrabold">
            {closing ? "Before you go…" : "Feedback & inputs"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Help us build the platform independent creators deserve. Takes 30 seconds.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="mono text-white/60">Overall rating</Label>
            <div className="flex gap-1 mt-2" data-testid="feedback-stars">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} data-testid={`feedback-star-${n}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}>
                  <Star className={`w-7 h-7 transition ${(hover || rating) >= n ? "fill-kinetic text-kinetic" : "text-white/25"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mono text-white/60">How was your overall experience?</Label>
            <Textarea data-testid="feedback-experience" value={experience} onChange={e => setExperience(e.target.value)} placeholder="What did you enjoy? What felt clunky?" className="bg-black border-white/15 rounded-sm mt-1 min-h-20" />
          </div>
          <div>
            <Label className="mono text-white/60">Any feedback or inputs?</Label>
            <Textarea data-testid="feedback-feedback" value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Bugs, ideas, complaints…" className="bg-black border-white/15 rounded-sm mt-1 min-h-16" />
          </div>
          <div>
            <Label className="mono text-white/60">Features you'd love to see</Label>
            <Textarea data-testid="feedback-features" value={features} onChange={e => setFeatures(e.target.value)} placeholder="What would make INDIEWOOD indispensable for you?" className="bg-black border-white/15 rounded-sm mt-1 min-h-16" />
          </div>
          <div>
            <Label className="mono text-white/60">A short review of the platform</Label>
            <Textarea data-testid="feedback-review" value={review} onChange={e => setReview(e.target.value)} placeholder="Would you recommend INDIEWOOD?" className="bg-black border-white/15 rounded-sm mt-1 min-h-16" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {closing && <Button data-testid="feedback-skip" variant="outline" onClick={skip} className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">Skip &amp; leave</Button>}
          <Button data-testid="feedback-submit" onClick={submit} disabled={busy} className="kinetic-btn rounded-sm">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Submit feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
